import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { UnitConverter } from "../numericInput";
import { MeasurementUnits, QualifierFragment } from "fe-common/graphql";

export type UnitConverterProps = {
  rangeItems: QualifierFragment[];
  unitConverters: UnitConverter[];
  value: number;
  setValue: Dispatch<SetStateAction<number>>;
  placeholder?: string;
  setPlaceholder?: Dispatch<SetStateAction<string>>;
};

export type CurrentUnitConverter = UnitConverter & {
  fromOldToNew: (number) => number;
  // Converts from the range units to the current units
  convertFromRange: (number) => number;
};

export interface UnitConverterApi {
  handleUnitChange: (UnitConverter) => void;
  currentUnitConverter: CurrentUnitConverter;
}

const Identity = (i) => i;

// Use this hook to allow unit conversion
export const useUnitConverter: (UnitConverterProps) => UnitConverterApi = ({
  rangeItems,
  unitConverters,
}: UnitConverterProps) => {
  const rangeUnits = useMemo(
    () => rangeItems?.[0]?.range?.units || MeasurementUnits.Units,
    [rangeItems],
  );

  const [currentUnitConverter, setCurrentUnitConverter] = useState<
    UnitConverter
  >(null);

  const [fromOldToNew, setFromOldToNew] = useState<(number) => number>(
    () => Identity,
  );

  // Make sure to make the default units based on the ones we got in the range
  // make sure to update the converter everytime the props change
  useEffect(() => {
    if (!rangeUnits || !unitConverters?.length) {
      return;
    }
    const newUnitConverter = unitConverters.find((u) => u.unit === rangeUnits);
    setCurrentUnitConverter((oldUnitConverter) => {
      setFromOldToNew(
        newUnitConverter?.converters?.find(
          (converter) => converter.from === oldUnitConverter?.unit,
        )?.converter || Identity,
      );
      return newUnitConverter;
    });
  }, [rangeUnits, unitConverters]);

  // Converts the units the user currently uses to one the ones the solver
  // range supports, if its even needed
  const convertFromRange: (number) => number = useCallback(
    (_value) => {
      if (!currentUnitConverter || rangeUnits === currentUnitConverter.unit)
        return _value;

      return currentUnitConverter.converters
        .find((c) => c.from === rangeUnits)
        .converter(_value);
    },
    [rangeUnits, currentUnitConverter],
  );

  const handleUnitChange = (newUnit: UnitConverter) => {
    if (newUnit === currentUnitConverter) return;

    const fromOldToNewConverter = newUnit.converters.find(
      (c) => c.from === currentUnitConverter.unit,
    ).converter; //find the converter functions

    // Use a callback to make sure react don't think the function is a callback
    setFromOldToNew(() => fromOldToNewConverter || Identity);

    setCurrentUnitConverter(newUnit);
  };

  const memoizedUnitConverter = useMemo(
    () => ({
      ...currentUnitConverter,
      convertFromRange,
      fromOldToNew,
    }),
    [currentUnitConverter, convertFromRange, fromOldToNew],
  );

  return {
    handleUnitChange,
    currentUnitConverter: memoizedUnitConverter,
  } as UnitConverterApi;
};
